import moment from "moment";
import { DATE_TYPE_CODES, formats } from "../../config/constants";
import languageModel from "../../models/language.model";
import ToggleButtonGroupComponent from "../common/toggle-button-group.component";
import DatePickerComponent from "../date-picker/date-picker.component";

/**
 * DynamicDateComponent
 * 
 * Dynamic Date components that supports Date, Week and Month based on
 * a given support date types.
 * 
 * @author Darrus Goh 
 */
export default function DynamicDateComponent() {
    const DEFUALT_ONCHANGE = () => { console.log('DynamicDateComponent: onChange(selectedDateType, fromDate, toDate)') };

    const DATE_TYPE_FORMATS = {
        [DATE_TYPE_CODES.WEEK]: formats.WEEK_FORMAT,
        [DATE_TYPE_CODES.MONTH]: formats.MONTH_FORMAT,
        [DATE_TYPE_CODES.DATE]: formats.dateFormat
    };

    const DATE_TYPE_UNITS = {
        [DATE_TYPE_CODES.WEEK]: 'week',
        [DATE_TYPE_CODES.MONTH]: 'month',
        [DATE_TYPE_CODES.DATE]: 'day'
    };

    const getDateLanguageText = (dateType) => {
        switch (dateType) {
            case DATE_TYPE_CODES.DATE:
                return languageModel.getText('LT00000132', 'Date');
            case DATE_TYPE_CODES.WEEK:
                return languageModel.getText('LT00000337', 'Week');
            case DATE_TYPE_CODES.MONTH:
                return languageModel.getText('LT00000326', 'Month');
            case DATE_TYPE_CODES.QUARTER:
                    return 'Quarter';
            default:
                return '';
        }
    };

    const dateInput = {
        view: ({ attrs: {
            dateType,
            fromDate,
            toDate,
            minDate,
            maxDate,
            min,
            max,
            required,
            onChange
        } }) => {
            switch (dateType) {
                case DATE_TYPE_CODES.WEEK:
                    {
                        return [
                            m('.col-6.position-relative',
                                m(`.easepick-input-group${required && isEmpty(fromDate) ? '.is-invalid-custom' : ''}`,
                                    m('input.form-control', {
                                        type: 'week',
                                        min: formatDate(minDate, DATE_TYPE_CODES.DATE, DATE_TYPE_CODES.WEEK),
                                        max: formatDate(maxDate, DATE_TYPE_CODES.DATE, DATE_TYPE_CODES.WEEK),
                                        value: fromDate,
                                        required,
                                        onchange: (e) => {
                                            let newFromDate = e.target.value;
                                            onChange(dateType, newFromDate, calculateNewToDate(newFromDate, toDate, minDate, maxDate, min, max, dateType));
                                        }
                                    })
                                ),
                                m('.invalid-tooltip', languageModel.getText("LT00000729", "This field is required"))
                            ),
                            m('.col-6.position-relative',
                                m(`.easepick-input-group${required && isEmpty(toDate) ? '.is-invalid-custom' : ''}`,
                                    m('input.form-control', {
                                        type: 'week',
                                        min: determineToDateLowerbound(fromDate, minDate, min, dateType),
                                        max: determineToDateUpperbound(fromDate, maxDate, max, dateType),
                                        value: toDate,
                                        required,
                                        onchange: (e) => {
                                            onChange(dateType, fromDate, e.target.value);
                                        }
                                    })
                                ),
                                m('.invalid-tooltip', languageModel.getText("LT00000729", "This field is required"))
                            )
                        ];
                    }
                case DATE_TYPE_CODES.MONTH:
                    {
                        return [
                            m('.col-6.position-relative',
                                m(`.easepick-input-group${required && isEmpty(fromDate) ? '.is-invalid-custom' : ''}`,
                                    m('input.form-control', {
                                        type: 'month',
                                        min: formatDate(minDate, DATE_TYPE_CODES.DATE, DATE_TYPE_CODES.MONTH),
                                        max: formatDate(maxDate, DATE_TYPE_CODES.DATE, DATE_TYPE_CODES.MONTH),
                                        value: fromDate,
                                        required,
                                        onchange: (e) => {
                                            let newFromDate = e.target.value;
                                            onChange(dateType, newFromDate, calculateNewToDate(newFromDate, toDate, minDate, maxDate, min, max, dateType));
                                        }
                                    })
                                ),
                                m('.invalid-tooltip', languageModel.getText("LT00000729", "This field is required"))
                            ),
                            m('.col-6.position-relative',
                                m(`.easepick-input-group${required && isEmpty(toDate) ? '.is-invalid-custom' : ''}`,
                                    m('input.form-control', {
                                        type: 'month',
                                        min: determineToDateLowerbound(fromDate, minDate, min, dateType),
                                        max: determineToDateUpperbound(fromDate, maxDate, max, dateType),
                                        value: toDate,
                                        required,
                                        onchange: (e) => {
                                            onChange(dateType, fromDate, e.target.value);
                                        }
                                    })
                                ),
                                m('.invalid-tooltip', languageModel.getText("LT00000729", "This field is required"))
                            )
                        ]
                    };
                case DATE_TYPE_CODES.DATE:
                    {
                        return m('.col-12.position-relative',
                            m(`${required && eitherIsEmpty(fromDate, toDate) ? '.is-invalid-custom' : ''}`,
                                m(DatePickerComponent, {
                                    class: 'form-control',
                                    dateStart: fromDate,
                                    dateEnd: toDate,
                                    minDate: minDate,
                                    maxDate: maxDate,
                                    minDays: min,
                                    maxDays: max,
                                    isRange: true,
                                    required,
                                    onselect: (e) => {
                                        onChange(dateType, e.detail.start, e.detail.end);
                                    }
                                })
                            ),
                            m('.invalid-tooltip', languageModel.getText("LT00000729", "This field is required"))
                        );
                    }
                default:
                    return null;
            }
        }
    };

    const calculateNewToDate = (fromDate, toDate, minDate, maxDate, minAmount, maxAmount, dateType) => {
        if (isEmpty(toDate)) {
            return toDate;
        }

        const lowerboundDate = determineToDateLowerbound(fromDate, minDate, minAmount, dateType);
        const upperboundDate = determineToDateUpperbound(fromDate, maxDate, maxAmount, dateType);

        return getDateWithinBounds(toDate, lowerboundDate, upperboundDate, dateType);
    };

    const getDateWithinBounds = (date, minDate, maxDate, dateType) => {
        const dateFormat = DATE_TYPE_FORMATS[dateType];
        const momentDate = moment(date, dateFormat);
        const momentMinDate = moment(minDate, dateFormat);
        const momentMaxDate = moment(maxDate, dateFormat);

        if (momentDate < momentMinDate) {
            return momentMinDate.format(dateFormat);
        } else if (momentDate > momentMaxDate) {
            return momentMaxDate.format(dateFormat);
        } else {
            return momentDate.format(dateFormat);
        }
    }

    const determineToDateLowerbound = (fromDate, minDate, minAmount, dateType) => {
        // If no from date selected means there's no starting point so just return minimum allowed date
        if (isEmpty(fromDate)) {
            return formatDate(minDate, DATE_TYPE_CODES.DATE, dateType);
        }

        const dateFormat = DATE_TYPE_FORMATS[dateType];
        const momentNewLowerBoundDate = moment(fromDate, dateFormat);

        if (minAmount > 0) {
            // Subtract by 1 because including current day
            momentNewLowerBoundDate.add(minAmount - 1, DATE_TYPE_UNITS[dateType]);
        }

        return momentNewLowerBoundDate.format(dateFormat);
    }

    const determineToDateUpperbound = (fromDate, maxDate, maxAmount, dateType) => {
        // If no from date selected means there's no starting point so just return maximum allowed date
        if (isEmpty(fromDate)) {
            return formatDate(maxDate, DATE_TYPE_CODES.DATE, dateType);
        }

        const dateFormat = DATE_TYPE_FORMATS[dateType];
        const momentMaxDate = moment(maxDate, dateFormat);
        if (maxAmount > 0) {
            // Subtract by 1 because including current day
            const momentNewUpperBoundDate = moment(fromDate, dateFormat).add(maxAmount - 1, DATE_TYPE_UNITS[dateType]);
            if (momentNewUpperBoundDate < momentMaxDate) {
                return momentNewUpperBoundDate.format(dateFormat);
            }
        }

        return momentMaxDate.format(dateFormat);
    }

    const formatDate = (date, fromDateType, toDateType) => {
        if (isEmpty(date))
            return '';

        let fromDateFormat = DATE_TYPE_FORMATS[fromDateType];
        let toDateFormat = DATE_TYPE_FORMATS[toDateType];

        return moment(date, fromDateFormat).format(toDateFormat);
    }

    const eitherIsEmpty = (fromDate, toDate) => {
        return isEmpty(fromDate) || isEmpty(toDate);
    };

    const isEmpty = (date) => {
        return date === null || date === '';
    }

    /** TODO: Inaccurate min/max
     * @see https://code.sdsdev.co.kr/mcssea/mcs-web/issues/923
     */
    const handleOnChange = (fromDate, toDate, minDate, maxDate, min, max, currentSelectedDateType, onChange) => (e) => {
        const newSelectedDateType = e.target.id;

        let newFromDate = formatDate(fromDate, currentSelectedDateType, newSelectedDateType);

        // Get last date of current date type and format to new date type
        let newToDate = '';
        if (!isEmpty(toDate)) {
            newToDate = moment(toDate, DATE_TYPE_FORMATS[currentSelectedDateType])
                .endOf(DATE_TYPE_UNITS[currentSelectedDateType])
                .format(DATE_TYPE_FORMATS[newSelectedDateType]);
        }

        newToDate = calculateNewToDate(newFromDate, newToDate, minDate, maxDate, min, max, newSelectedDateType);

        onChange(e.target.id, newFromDate, newToDate);
    }

    return {
        view: ({ attrs: {
            selectedDateType,
            supportedDates = [],
            fromDate = '',
            toDate = '',
            minDate = moment().date(1).month(0).subtract(100, 'years').format(DATE_TYPE_FORMATS[DATE_TYPE_CODES.DATE]),
            maxDate = moment().date(31).month(11).add(100, 'years').format(DATE_TYPE_FORMATS[DATE_TYPE_CODES.DATE]),
            min = 0,
            max = 0,
            required = false,
            label = languageModel.getText('LT00000150', 'Period'),
            onChange = DEFUALT_ONCHANGE
        } }) => supportedDates.length > 0 && [
            m('.hstack.justify-content-between.align-items-end.form-label',
                m(`label${required && '.mandatory'}`, label),
                supportedDates.length > 1 && m(ToggleButtonGroupComponent, {
                    inline: true,
                    buttonType: 'radio',
                    onChange: handleOnChange(fromDate, toDate, minDate, maxDate, min, max, selectedDateType, onChange),
                    buttons: supportedDates.map(type => {
                        return {
                            id: type,
                            label: getDateLanguageText(type),
                            checked: selectedDateType === type
                        };
                    })
                }),
            ),
            m('.row.g-1',
                m(dateInput, {
                    dateType: selectedDateType,
                    fromDate,
                    toDate,
                    minDate,
                    maxDate,
                    min,
                    max,
                    required,
                    onChange
                })
            )
        ]
    }
}